// Copyright 2025, Imprivata, Inc.  All rights reserved.

import ContinueButton from '@components/continueButton/ContinueButton';
import PasswordStrengthMessage from './PasswordStrengthMessage';
import { ErrorCode, ErrorMessage } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { Banner, InputBox } from '@imprivata-cloud/components';
import { useChangePassword } from '@selfservice/hooks/useChangePassword';
import { useGetPasswordPolicy } from '@/self-service/hooks/useGetPasswordPolicy';
import { Form } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import type { ApiError } from '@errorHandler/errors';
import { AppError } from '@errorHandler/errors';

import './IdentityPassword.less';
import { RootRoutes } from '@/index';
import { buildPasswordStrengthRegex, mapPasswordPolicyResponse } from '@selfservice/utils/Regex';

enum fieldName {
  PASSWORD = 'password',
  CONFIRM_PASSWORD = 'confirmPassword',
}

interface AuthnPassword {
  [fieldName.PASSWORD]: string;
  [fieldName.CONFIRM_PASSWORD]: string;
}

const TRANSLATION_ROOT = 'self-service.auth-method.password';

const IdentityPassword = () => {
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [form] = Form.useForm<AuthnPassword>();
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const navigateKeepSearch = useNavigateKeepSearch();
  const location = useLocation();

  const {
    data: policyData,
    isLoading: isLoadingPolicy,
    error: policyError,
  } = useGetPasswordPolicy(false);
  const { mutate: changePassword, isPending: isSubmittingPassword } = useChangePassword();

  const password = Form.useWatch(fieldName.PASSWORD, form) || '';
  const confirmPassword = Form.useWatch(fieldName.CONFIRM_PASSWORD, form) || '';

  const passwordPolicy = useMemo(() => {
    return policyData ? mapPasswordPolicyResponse(policyData) : null;
  }, [policyData]);

  useEffect(() => {
    if (policyError) {
      console.log('Error fetching password policy:', policyError);
      emitError(AppError.fromMessage(ErrorMessage.SYSTEM_ERROR, ErrorCode.UNKNOWN));
    }
  }, [policyError, emitError]);

  const passwordStrengthRegex = useMemo(() => {
    if (!passwordPolicy) {
      return /^.{8,256}$/;
    }
    return buildPasswordStrengthRegex(passwordPolicy);
  }, [passwordPolicy]);

  const isPasswordMatch = useMemo(() => {
    if (password.length === 0 || confirmPassword.length === 0) {
      return true;
    }
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const isButtonDisabled = useMemo(() => {
    const isValid = password === confirmPassword && passwordStrengthRegex.test(password);
    return !isValid || !!passwordError;
  }, [password, confirmPassword, passwordStrengthRegex, passwordError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:Only clear error when password changes
  useEffect(() => {
    if (passwordError && password) {
      setPasswordError(null);
    }
  }, [password]);

  const handleSubmit = async (values: AuthnPassword) => {
    changePassword(
      { newPassword: values.password },
      {
        onSuccess: () => {
          navigateKeepSearch(RootRoutes.SELF_SERVICE);
        },
        onError: (e: unknown) => {
          console.log('Error changing password:', e);
          const apiError = e as ApiError;
          if (apiError.code === ErrorCode.PASSWORD_REUSED) {
            setPasswordError(t(`${TRANSLATION_ROOT}.password-reused-error`));
          } else if (apiError.code === ErrorCode.INVALID_PASSWORD) {
            setPasswordError(t(`${TRANSLATION_ROOT}.password-invalid-error`));
          } else {
            emitError(new AppError(apiError));
          }
        },
      },
    );
  };

  const handleDoThisLater = () => {
    const previousRoute = location.state?.from;
    navigateKeepSearch(previousRoute || RootRoutes.SELF_SERVICE);
  };

  return (
    <div className="identity-password">
      {passwordError && (
        <Banner type="error" message={passwordError} duration={0} closable={false} />
      )}
      <div className="title">{t(`${TRANSLATION_ROOT}.title`)}</div>
      <div className="sub-title" />

      <Form form={form} layout="vertical" onFinish={handleSubmit} validateTrigger={[]}>
        {/* PASSWORD */}
        <Form.Item
          name={fieldName.PASSWORD}
          rules={[
            {
              required: true,
              min: passwordPolicy?.minimumLength || 8,
              max: passwordPolicy?.maximumLength || 256,
            },
          ]}
          extra={
            !isLoadingPolicy && passwordPolicy ? (
              <Form.Item shouldUpdate>
                {() => (
                  <PasswordStrengthMessage
                    passwordPolicy={passwordPolicy}
                    currentPassword={form.getFieldValue(fieldName.PASSWORD) || ''}
                  />
                )}
              </Form.Item>
            ) : null
          }
        >
          <InputBox
            label={t(`${TRANSLATION_ROOT}.password`)}
            autoFocus
            type="password"
            aria-label="password"
            className={passwordError ? 'password-error-input' : undefined}
          />
        </Form.Item>

        {/* CONFIRM PASSWORD */}
        <Form.Item
          name={fieldName.CONFIRM_PASSWORD}
          rules={[
            {
              required: true,
              min: passwordPolicy?.minimumLength || 8,
              max: passwordPolicy?.maximumLength || 256,
            },
          ]}
        >
          <InputBox
            label={t(`${TRANSLATION_ROOT}.confirm-password`)}
            type="password"
            aria-label="confirm-password"
            className={passwordError || !isPasswordMatch ? 'password-error-input' : undefined}
          />
        </Form.Item>
        <div className="password-mismatch">
          {!isPasswordMatch && <span>{t(`${TRANSLATION_ROOT}.password-mismatch`)}</span>}
        </div>

        {/* CONTINUE or LATER */}
        <ContinueButton
          aria-label="continue-button"
          htmlType="submit"
          disabled={isButtonDisabled || isSubmittingPassword}
          loading={isSubmittingPassword}
          secondaryButton={{
            'aria-label': 'do-this-later-button',
            label: t('common.cancel'),
            onClick: handleDoThisLater,
          }}
        >
          {t('common.save')}
        </ContinueButton>
      </Form>
    </div>
  );
};

export default IdentityPassword;
